export type ProgressSpinnerSize = 'sm' | 'md' | 'lg'

interface ProgressSpinnerProps {
  /** Size preset: sm (10px), md (20px), lg (24px) */
  size?: ProgressSpinnerSize
  /** Custom size in pixels (overrides size preset) */
  customSize?: number
  className?: string
}

const SIZE_MAP: Record<ProgressSpinnerSize, number> = {
  sm: 10,
  md: 20,
  lg: 24
}

/**
 * Unified purple spinner used across progress indicators.
 * Used in: TaskProgressMessageView, TodoList, TimelineBlock
 */
export function ProgressSpinner({ size = 'md', customSize, className = '' }: ProgressSpinnerProps) {
  const pixelSize = customSize ?? SIZE_MAP[size]

  // Stroke width needs to be thicker for small sizes to remain visible
  // When viewBox is 24x24 scaled to 10px, strokes need to be proportionally thicker
  const strokeWidth = pixelSize <= 12 ? 2.5 : 2

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={pixelSize}
      height={pixelSize}
      viewBox='0 0 24 24'
      fill='none'
      className={`animate-spin ${className}`}
    >
      <path
        d='M12 2V6'
        stroke='#9D7EE6'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12 18V22'
        stroke='#846FE1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4.92969 4.92969L7.75969 7.75969'
        stroke='#846FE1'
        strokeOpacity='0.1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16.2402 16.2402L19.0702 19.0702'
        stroke='#846FE1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M2 12H6'
        stroke='#846FE1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M18 12H22'
        stroke='#846FE1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4.92969 19.0702L7.75969 16.2402'
        stroke='#846FE1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M16.2402 7.75969L19.0702 4.92969'
        stroke='#846FE1'
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

export default ProgressSpinner
